// F9: tour de holofote no primeiro login (sidebar, seletor de workspace, primeira ação, menu Admin).
// A conclusão fica no servidor, no próprio usuário (PATCH /api/v1/auth/preferencias), não no navegador.
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, type Eu } from '../api';

const PASSOS = ['sidebar', 'workspace', 'acao', 'admin'] as const;

function retanguloDe(alvo: string): DOMRect | null {
  const el = document.querySelector<HTMLElement>(`[data-tour="${alvo}"]`);
  if (!el || el.offsetParent === null) return null;
  return el.getBoundingClientRect();
}

export function Tour({ eu }: { eu: Eu }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const concluir = useMutation({
    mutationFn: () => api('/api/v1/auth/preferencias', { method: 'PATCH', json: { onboarding_concluido: true } }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['eu'] }),
  });
  const [i, setI] = useState(0);
  const [ret, setRet] = useState<DOMRect | null>(null);
  const [visiveis, setVisiveis] = useState<(typeof PASSOS)[number][]>([]);
  const ativo = !eu.usuario.onboardingConcluido && !concluir.isPending && !concluir.isSuccess;
  // Os alvos só existem depois que o shell montou: mede depois do paint. Passos sem alvo nesta tela ou para
  // este papel (ex.: sem Admin, sidebar recolhida no mobile) são pulados; sem nenhum alvo, nada aparece.
  useEffect(() => {
    if (!ativo) return;
    const h = setTimeout(() => setVisiveis(PASSOS.filter((p) => retanguloDe(p))), 400);
    return () => clearTimeout(h);
  }, [ativo]);
  const passo = visiveis[i];

  useLayoutEffect(() => {
    if (!passo) return;
    const medir = () => setRet(retanguloDe(passo));
    medir();
    window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, [passo]);
  if (!ativo || !passo || !ret) return null;
  const ultimo = i === visiveis.length - 1;
  const folga = 6;
  // Alvo alto (ex.: a sidebar inteira): cartão ao lado; senão, embaixo ou em cima conforme o espaço.
  const alto = ret.height > window.innerHeight * 0.5 && ret.right + 320 < window.innerWidth;
  const cartaoEmBaixo = ret.bottom + 200 < window.innerHeight;
  const posicao = alto
    ? { top: Math.max(16, ret.top + 96), left: ret.right + 16 }
    : { top: cartaoEmBaixo ? ret.bottom + 14 : Math.max(16, ret.top - 190), left: Math.max(16, Math.min(ret.left, window.innerWidth - 304)) };
  return createPortal(
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={t('tour.titulo')}>
      <div
        className="pointer-events-none absolute rounded-xl ring-2 ring-acento transition-all"
        style={{ top: ret.top - folga, left: ret.left - folga, width: ret.width + folga * 2, height: ret.height + folga * 2, boxShadow: '0 0 0 9999px var(--sombra)' }}
      />
      <div
        className="absolute w-72 rounded-xl border border-borda bg-sup-1 p-4 shadow-2xl"
        style={posicao}
      >
        <p className="kicker mb-1">{t('tour.passo', { n: i + 1, total: visiveis.length })}</p>
        <p className="mb-1 font-medium">{t(`tour.${passo}.titulo`)}</p>
        <p className="mb-4 text-sm text-tinta-2">{t(`tour.${passo}.texto`)}</p>
        <div className="flex justify-between gap-2">
          <button type="button" onClick={() => concluir.mutate()} className="h-9 rounded-lg px-3 text-sm text-tinta-3 hover:bg-sup-2 hover:text-tinta-1">{t('tour.pular')}</button>
          <button type="button" autoFocus onClick={() => (ultimo ? concluir.mutate() : setI(i + 1))} className="h-9 rounded-lg bg-acento px-4 text-sm font-medium text-acento-tinta hover:bg-acento-forte">
            {ultimo ? t('tour.concluir') : t('tour.proximo')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
